import React from 'react'

import { PartialRecord } from '@/data'
import { useNotificationError } from '@/modules/Notifications'

const useFormValues = <FormValues>(initValues: FormValues) => {
  const [addNotificationError] = useNotificationError()

  const [values, setValues] = React.useState<FormValues>(initValues)
  const [errors, setErrors] = React.useState<PartialRecord<keyof FormValues, string>>({})

  const isValid = React.useMemo(() => Object.keys(errors).every((key) => !errors[key]?.length), [errors])

  const setValue = <K extends keyof FormValues>(
    key: K,
    value: FormValues[K] | ((prevValue: FormValues[K]) => FormValues[K]),
  ) => {
    setValues((prev) => {
      if (typeof value === 'function') {
        const valueFunction = value as (prevValue: FormValues[K]) => FormValues[K]
        return {
          ...prev,
          [key]: valueFunction(prev[key]),
        }
      }

      return {
        ...prev,
        [key]: value,
      }
    })
  }

  const setError = <K extends keyof FormValues>(key: K, value: string) => {
    setErrors((prev) => {
      return {
        ...prev,
        [key]: value,
      }
    })
  }

  const manageNotificationError = (error: Error) => {
    const fields = addNotificationError({
      error,
    })

    if (fields) {
      setErrors((prev) => {
        return {
          ...prev,
          ...fields,
        }
      })
    }
  }

  return { values, errors, setValue, setError, setValues, setErrors, manageNotificationError, isValid } as const
}

export default useFormValues
